import { useEffect, useState, useRef } from 'react';
// next
import { useRouter } from 'next/router';
// layouts
import Layout from '../../layouts';
import {
  Box,
  Button,
  Typography,
  Container,
  Stack,
  Menu,
  MenuItem,
  Checkbox,
  Badge,
} from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
import useLocalStorage from '../../hooks/useLocalStorage';
// guards
import DefaultLoginGuard from '../../guards/DefaultLoginGuard';
// components
import Page from '../../components/Page';
import { MotionViewport } from '../../components/animate';
import Iconify from '../../components/Iconify';
import {
  JobPostCard,
  FAQ,
  SkeletonJobPostCard,
  GuideNewJobPostCard,
  AddNewJobPostCard,
  DIOReviewLink,
  BizMatchingTodo,
  BizMeetingTodo,
  JobPostOfferDialog,
  JobPostMatchingDialog,
} from '../../sections/jobs';
import {
  JobPostingDialog,
} from '../../sections/jobs/post';
import DIOText from '../../components/DIOText';
// routes
import { PATH_JOBS } from '../../routes/paths';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';

Jobs.getLayout = function getLayout(page) {
  return <Layout variant="nav">{page}</Layout>
}

export default function Jobs() {
  const { user } = useAuth();
  const router = useRouter();
  const { query } = router;

  const { mutate, data: jobPosts, isLoading: loading } = useSWR(user ? `/api/company/${user?.employee.company[0].id}/job-posts/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: true, revalidateOnMount: true});
  const [filteredJobPosts, setFilteredJobPosts] = useState([]);
  const [jobPost, setJobPost] = useState({});
  const [candidateId, setCandidateId] = useState(null);
  // dialog open/close
  const [isJobPostingDialogOpen, setIsJobPostingDialogOpen] = useState(false);
  const [isJobPostMatchingDialogOpen, setIsJobPostMatchingDialogOpen] = useState(false);
  const [isJobPostOfferDialogOpen, setIsJobPostOfferDialogOpen] = useState(false);
  // edit dialog
  const [isEdit, setIsEdit] = useState(false);
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');
  // widget ref
  const widgetRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  // filter
  const [anchorEl, setAnchorEl] = useState(null);
  const openFilter = Boolean(anchorEl);
  const [filters, setFilters] = useLocalStorage('dioJobPostFilterList', []);
  const showFilterBadge = filters?.length === 0;
  const filterOptions = [
    "작성 중",
    "매칭 진행 중",
    "매칭 중단",
    "계약 완료",
  ];

  const closeFilter = () => {
    setAnchorEl(null);
  };

  const selectFilter = (option) => {
    if (filters.includes(option)) {
      setFilters(filters.filter((filter) => filter !== option));
    } else {
      setFilters([...filters, option]);
    }
  };

  useEffect(() => {
    if (filters.length === 0) {
      setFilteredJobPosts(jobPosts || []);
    } else {
      const statusFilters = [];
      if (filters.includes("작성 중")) {
        statusFilters.push("position-set");
        statusFilters.push("method-direct");
        statusFilters.push("detail-position-set");
        statusFilters.push("context-set");
        statusFilters.push("method-paste");
        statusFilters.push("external-link-set");
      }
      if (filters.includes("매칭 진행 중")) {
        statusFilters.push("searching");
        statusFilters.push("offer-to-crew");
        statusFilters.push("offer-to-company");
        statusFilters.push("other-3way-time-requested");
        statusFilters.push("reselect-3way-time");
        statusFilters.push("waiting-3way-meeting");
        statusFilters.push("confirm-3way-meeting");
        statusFilters.push("waiting-offer-from-crew");
        statusFilters.push("crew-negotiate-to-company");
        statusFilters.push("company-negotiate-rewrite");
        statusFilters.push("task-set");
        statusFilters.push("review-requested");
        statusFilters.push("write-requested");
      }
      if (filters.includes("매칭 중단")) {
        statusFilters.push("feedback-meeting");
        statusFilters.push("set-meeting");
        statusFilters.push("waiting-no-hurry");
        statusFilters.push("stop");
      }
      if (filters.includes("계약 완료")) {
        statusFilters.push("complete");
      }
      setFilteredJobPosts(jobPosts?.filter((post) => statusFilters.includes(post.status)));
    }
  }, [jobPosts, filters]);

  const openDialog = (selectedJobPost) => {
    setJobPost(selectedJobPost);
    if (
      !selectedJobPost
      || ['position-set', 'method-paste', 'external-link-set', 'method-direct', 'detail-position-set', 'task-set', 'context-set', 'monthly-budget-set', 'write-position', 'feedback-rewrite', 'feedback-meeting', 'set-meeting'].includes(selectedJobPost.status)) {
      // 공고 작성
      setIsJobPostingDialogOpen(true);
    } else {
      // 매칭
      setIsJobPostMatchingDialogOpen(true);
    }
  };

  const editJobPost = () => {
    if (jobPost) {
      setIsEdit(true);
      setIsJobPostingDialogOpen(true);
      setIsJobPostMatchingDialogOpen(false);
    }
  }

  const closeJobPostDialog = async () => {
    setJobPost({});
    setCandidateId(null);
    setIsEdit(false);
    setIsJobPostingDialogOpen(false);
    setIsJobPostMatchingDialogOpen(false);
    setIsJobPostOfferDialogOpen(false);
  };

  const toggleDialog = async (selectedId) => {
    setIsJobPostingDialogOpen(false);
    setIsEdit(false);
    reloadJobPosts(selectedId);
  };

  const goToOfferDialog = async (candidateId) => {
    setIsJobPostOfferDialogOpen(true);
    setCandidateId(candidateId);
    setIsJobPostMatchingDialogOpen(false);
  };

  const backToMatchingDialog = async () => {
    setIsJobPostOfferDialogOpen(false);
    mutate();
    setCandidateId(null);
    setIsJobPostMatchingDialogOpen(true);
  };

  const reloadWithoutPush = async (selectedId) => {
    const data = await mutate();
    const newJobPost = data.find(jp => jp.id == selectedId);
    if (newJobPost) {
      setJobPost(newJobPost);
    }
  }

  const reloadJobPosts = async (selectedId) => {
    await reloadWithoutPush(selectedId);
    router.push({pathname: PATH_JOBS.root, query: {jid: selectedId}});
  };

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        setIsVisible(width >= 1000);
      }
    });

    if (widgetRef.current) {
      observer.observe(widgetRef.current);
    }

    return () => {
      if (widgetRef.current) {
        observer.unobserve(widgetRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isEdit) return ;
    else if (query && jobPosts?.length > 0 && query?.jid) {
      const post = jobPosts.find(jp => jp.id == query.jid);
      if (query?.cid) {
        setCandidateId(query.cid);
      }
      if (post) {
        openDialog(post);
      }
      router.push({pathname: PATH_JOBS.root, query: {} }, undefined, { shallow: true });
    } else if (query && query?.new === 'y') {
      openDialog();
    } else if (jobPosts?.length === 1 && jobPosts[0].auto_created_when_sign_up) {
      const post = jobPosts[0];
      if (['position-set'].includes(post.status)) {
        openDialog(post);
      }
    } else {
      // setJobPost(jobPost);
    }
  }, [jobPosts, query, isEdit]);

  return (
    <DefaultLoginGuard>
      <Page title="Home" sx={{ px: 3 }}>
        <Container maxWidth={"xl"} disableGutters>
        {/* title */}
        <Typography variant="h5" paragraph>
          지금 검증된 경력자를 만나보세요.
        </Typography>

        {/* 해야할 일 & PC면 후기 */}
        <Stack
          ref={widgetRef}
          direction={mdUp ? "row" : "column"} alignItems="flex-start" spacing={3} sx={{ height: mdUp ? "242px" : 1 }}
        >
          <BizMatchingTodo jobPosts={jobPosts} addNewJobPost={() => openDialog()}/>
          <BizMeetingTodo jobPosts={jobPosts} addNewJobPost={() => openDialog()}/>
          {mdUp && isVisible && (<DIOReviewLink />)}
        </Stack>

        {/* 채용 진행상황 */}
        <Box sx={{ mt: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"}>
              <DIOText /> 크루 채용 공고
            </Typography>
            <>
              <Badge
                sx={{
                  width: 28,
                  height: 28,
                }}
                variant="dot"
                color="info"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                overlap="circular"
                invisible={showFilterBadge}
              >
                <Box
                  size="medium"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  aria-label="filter"
                  aria-controls="filter-menu"
                  aria-haspopup="true"
                  aria-expanded={openFilter ? 'true' : undefined}
                  color='primary'
                  sx={{
                    ":hover": {
                      cursor: 'pointer',
                      backgroundColor: 'grey.300',
                    },
                    ":active": {
                      backgroundColor: (theme) => theme.palette.grey[500_24],
                    },
                    bgcolor: openFilter ? (theme) => theme.palette.grey[500_16] : "none",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                  }}
                >
                  <Iconify icon="ic:round-filter-list" sx={{ width: 24, height: 24 }} />
                </Box>
              </Badge>
              <Menu
                id="filter-menu"
                MenuListProps={{
                  'aria-labelledby': 'filter-menu',
                }}
                anchorEl={anchorEl}
                open={openFilter}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{ vertical: 'top', horizontal: 'right'}}
                onClose={closeFilter}
                sx={{
                  '& .MuiMenu-paper': {
                    width: 140,
                  },
                }}
              >
                {filterOptions.map((option, index) => (
                  <MenuItem key={index} sx={{ px: 1, py: 0.5 }} onClick={() => selectFilter(option)}>
                    <Checkbox size="small" checked={filters?.includes(option) || false} />
                    <Typography variant="caption">{option}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </>
            </Stack>
            <Button
              onClick={() => openDialog()}
              startIcon={<Iconify icon="eva:plus-fill" />}
              size={smUp ? "medium" : "small"}
              variant="contained"
              sx={{ boxShadow: "none"}}
              disableRipple
            >
              새 공고 작성하기
            </Button>
          </Stack>
          <Box
            component={MotionViewport}
            sx={{
              mt: 2,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(3, 1fr)',
                xl: 'repeat(4, 1fr)',
              },
            }}
          >
            {smUp && (<AddNewJobPostCard add={() => openDialog()} />)}
            {!loading && filteredJobPosts?.map((post, index) => (
              <JobPostCard key={index} jobPost={post} onClick={() => openDialog(post)} isLoading={loading} reloadJobs={closeJobPostDialog}/>
            ))}
            {!loading && filteredJobPosts?.length === 0 && (
              <GuideNewJobPostCard hasJobPost={jobPosts?.length > 0} />
            )}
            {loading && (
              <>
                <SkeletonJobPostCard />
                <SkeletonJobPostCard />
                <SkeletonJobPostCard />
              </>
            )}
          </Box>
        </Box>
        {!mdUp && (<DIOReviewLink />)}
        <JobPostingDialog
          jobPost={jobPost}
          isOpen={isJobPostingDialogOpen}
          isEdit={isEdit}
          editJobPost={editJobPost}
          closeDialog={closeJobPostDialog}
          toggleDialog={toggleDialog}
          reload={reloadWithoutPush}
        />
        <JobPostMatchingDialog
          jobPost={jobPost}
          candidateId={candidateId}
          isOpen={isJobPostMatchingDialogOpen}
          closeDialog={closeJobPostDialog}
          editJobPost={editJobPost}
          openJobPostOfferDialog={goToOfferDialog}
          reloadJobs={reloadJobPosts}
        />
        <JobPostOfferDialog
          jobPost={jobPost}
          candidateId={candidateId}
          isOpen={isJobPostOfferDialogOpen}
          closeDialog={closeJobPostDialog}
          backToMatchingDialog={backToMatchingDialog}
          reload={reloadJobPosts}
        />
        <FAQ />
        </Container>
      </Page>
    </DefaultLoginGuard>
  )
};
